// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import React from 'react';
import {
  Image,
  StyleSheet,
  Text,
  View,
  type ImageSourcePropType,
  type ImageStyle,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import GhostAvatar from '@/components/GhostAvatar';
import appColors from '@/theme/colors';

type PlayerAvatarProps = {
  /** Outer diameter in pixels. */
  size?: number;
  name?: string | null;
  /** Resolved photo, from getPlayerAvatarImageSource. Null when the player has no image. */
  source?: ImageSourcePropType | null;
  /** AI opponents and ghosts never have a photo — they get the ghost avatar instead. */
  isAi?: boolean;
  style?: StyleProp<ViewStyle>;
};

export function getPlayerInitials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/**
 * The player's picture wherever a leaderboard names them. A player without an uploaded
 * photo keeps the same round slot — initials on a circle — so rows stay aligned whether
 * or not anyone in the round has an image.
 */
export default function PlayerAvatar({ size = 24, name, source, isAi = false, style }: PlayerAvatarProps) {
  if (isAi) {
    return <GhostAvatar size={size} style={style} />;
  }

  const circle = { width: size, height: size, borderRadius: size / 2 };
  if (source) {
    return (
      <Image
        source={source}
        style={[styles.image, circle, style as StyleProp<ImageStyle>]}
        accessibilityLabel={name ?? undefined}
      />
    );
  }

  return (
    <View style={[styles.fallback, circle, style]} accessibilityRole="image" accessibilityLabel={name ?? undefined}>
      <Text style={[styles.fallbackText, { fontSize: Math.max(8, Math.round(size * 0.42)) }]}>
        {getPlayerInitials(name)}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  image: {
    backgroundColor: appColors.background.panelAlt,
  },
  fallback: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  fallbackText: {
    color: appColors.text.heading,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
});
